import type { paths } from "../deferred-api/contracts";
import type { AdminRepository } from "./admin-repository";
import { assertRequestAuthority, captureRequestAuthority } from "./authority";
import type {
  CanonicalCityCreateInput,
  CanonicalCityRecord,
  CanonicalCityUpdateInput,
  DestinationCreateInput,
  DestinationLifecycleInput,
  DestinationReferenceRecord,
  DestinationUpdateInput,
  ExperienceFilters,
  ReferenceFilters,
  SystemExperienceRecord,
} from "./catalogue-models";
import type {
  ExperienceOfferingFilters,
  ExperienceOfferingPage,
  PartnerHistoryFilters,
  PartnerHistoryPage,
  ScheduleOverviewFilters,
  ScheduleOverviewPage,
} from "./catalogue-operations-models";
import { apiClient } from "./client";
import { assertContactLifecycleTarget } from "./contact-lifecycle";
import type { DashboardFilters, DashboardSummary } from "./dashboard-models";
import { CyraError } from "./errors";
import type {
  ActionPolicy,
  ActionPolicyInput,
  ApprovalActionCode,
  ApprovalApplyInput,
  ApprovalExecutionReleaseInput,
  ApprovalRetryInput,
  ApprovalReviseInput,
  ApprovalWithdrawInput,
  ExecutionControlInput,
  SensitiveProjection,
  SensitiveReadControlInput,
} from "./governance-models";
import {
  defaultInvitationFilters,
  type InvitationFilters,
  type InvitationPage,
  type UserMutationReceipt,
} from "./identity-models";
import { importInvestigationHeader } from "./import-investigation";
import { importStatusValidator, rememberImportStatusValidator } from "./import-status-validator";
import type {
  AdminNotification,
  ApprovalModule,
  ApprovalPolicy,
  ApprovalRequest,
  ApprovalRequestDetail,
  ApprovalRequestFilterInput,
  AuditTrailDetail,
  AuditTrailFilters,
  AuditTrailItem,
  DestinationRecord,
  ExperienceCreateInput,
  ExperienceInput,
  ExperienceRecord,
  InvitationRecord,
  InvitationState,
  InviteInput,
  LegalAcceptance,
  LegalDocument,
  LegalDraftInput,
  PageResult,
  PartnerContactInput,
  PartnerContactLifecycleTarget,
  PartnerContactRecord,
  PartnerContactUpdateInput,
  PartnerCreateInput,
  PartnerFilters,
  PartnerImportBatch,
  PartnerImportChange,
  PartnerImportDefinition,
  PartnerImportIssue,
  PartnerOfferingCreateInput,
  PartnerOfferingInput,
  PartnerOfferingRecord,
  PartnerRecord,
  PartnerScheduleCreateInput,
  PartnerScheduleInput,
  PartnerSchedulePreview,
  PartnerSchedulePreviewInput,
  PartnerScheduleRecord,
  PartnerTeamInviteInput,
  PartnerTeamMember,
  PartnerTeamMembershipInput,
  PartnerUpdateInput,
  RoleCode,
  RoleDefinition,
  SessionRecord,
  UserFilters,
  UserRecord,
} from "./models";
import type {
  PartnerContactErasureInput,
  PartnerContactErasureInventory,
  PartnerContactErasureStatus,
  PartnerContactEvidenceHold,
  PartnerContactEvidenceHoldInput,
  PartnerContactFilters,
  PartnerContactPage,
  PartnerOfferingFilters,
  PartnerOfferingPage,
  PartnerScheduleFilters,
  PartnerSchedulePage,
} from "./partner-child-models";
import {
  type ImportCsvExport,
  type ImportFiltersByKind,
  type ImportInvestigation,
  type ImportPageByKind,
  type ImportRegenerateInput,
  type ImportReviewKind,
  type ImportStageInput,
  importReviewPaths,
} from "./partner-import-models";
import { throwApiError as requestFailed } from "./response-error";
import { ApiScheduleSeriesRepository } from "./schedule-series-api";
import { mutationHeaders, sessionHeaders } from "./session-headers";

type UserListItem =
  paths["/v1/admin/users"]["get"]["responses"][200]["content"]["application/json"]["items"][number];
type UserDetail =
  paths["/v1/admin/users/{userId}"]["get"]["responses"][200]["content"]["application/json"];
type AdministratorItem =
  paths["/v1/admin/administrators"]["get"]["responses"][200]["content"]["application/json"]["items"][number];
type InvitationItem =
  paths["/v1/admin/user-invitations"]["get"]["responses"][200]["content"]["application/json"]["items"][number];
type PartnerTeamItem =
  paths["/v1/admin/partners/{partnerId}/team"]["get"]["responses"][200]["content"]["application/json"]["items"][number];
type LegalItem =
  paths["/v1/admin/legal-document-versions"]["get"]["responses"][200]["content"]["application/json"]["items"][number];
type ApiAuditTrailItem =
  paths["/v1/admin/audit-trail"]["get"]["responses"][200]["content"]["application/json"]["items"][number];

const roleCodes = new Set<RoleCode>(["USER", "ADMIN", "MODERATOR", "CONTENT_EDITOR"]);
function toRoles(values: string[]): RoleCode[] {
  return values.filter((value): value is RoleCode => roleCodes.has(value as RoleCode));
}

function baseUser(item: UserListItem | UserDetail): UserRecord {
  const detail = "effectivePermissions" in item ? item : null;
  return {
    activeSessionCount: item.activeSessions,
    createdAt: item.createdAt,
    effectivePermissions: detail?.effectivePermissions ?? [],
    email: item.email,
    emailVerifiedAt: item.emailVerifiedAt,
    firstName: item.firstName,
    id: item.id,
    lastActiveAt: item.lastActiveAt,
    lastName: item.lastName,
    legalAcceptances: [],
    mfaEnrolled: false,
    profileImageAssetId: null,
    roles: toRoles(item.roles),
    sessions: [],
    state: item.state,
    updatedAt: detail?.updatedAt ?? item.createdAt,
    version: item.version,
  };
}

function mapAdministrator(item: AdministratorItem): UserRecord {
  return {
    activeSessionCount: item.activeSessions,
    createdAt: 0,
    effectivePermissions: [],
    email: item.email,
    emailVerifiedAt: null,
    firstName: item.firstName,
    id: item.id,
    isCurrentUser: item.isCurrentUser,
    lastAdminProtected: item.lastAdminProtected,
    lastActiveAt: null,
    lastName: item.lastName,
    legalAcceptances: [],
    mfaEnrolled: item.mfaEnrolledAt !== null,
    profileImageAssetId: null,
    roles: ["USER", ...toRoles(item.roles)],
    sessions: [],
    state: item.state,
    updatedAt: 0,
    version: item.version,
  };
}

function mapInvitation(item: InvitationItem): InvitationRecord {
  return {
    acceptedAt: item.acceptedAt,
    acceptedUserId: item.acceptedUserId,
    activatedAt: item.activatedAt,
    email: item.email,
    expiresAt: item.expiresAt,
    firstName: item.firstName,
    id: item.id,
    lastName: item.lastName,
    partnerAccess: item.partnerAccess,
    reason: item.reason,
    roles: ["USER", ...toRoles(item.roles.filter((role) => role !== "USER"))],
    sentAt: item.createdAt,
    state: item.state as InvitationState,
    updatedAt: item.updatedAt,
    version: item.version,
  };
}

function mapPartnerTeamMember(item: PartnerTeamItem): PartnerTeamMember {
  return {
    ...mapInvitation(item),
    membershipId: item.membershipId,
    membershipState: item.membershipState,
    membershipVersion: item.membershipVersion,
    partnerAccess: item.partnerAccess,
    roles: ["USER", "PARTNER_CONTENT_MANAGER"],
  };
}

function mapLegal(item: LegalItem): LegalDocument {
  return {
    acceptanceCount: item.acceptanceCount,
    bodyHtml: "body" in item && typeof item.body === "string" ? item.body : "",
    current: item.isCurrent,
    effectiveAt: item.effectiveAt,
    id: item.id,
    kind: item.kind === "TERMS" ? "terms" : "privacy",
    publishedAt: item.publishedAt,
    revision: item.version,
    state: item.state,
    title: item.title,
    updatedAt: item.updatedAt,
    version: item.semanticVersion,
  };
}

function mapAuditTrail(item: ApiAuditTrailItem): AuditTrailItem {
  return { ...item };
}

function mapPartner(
  item:
    | paths["/v1/admin/partners/{partnerId}"]["get"]["responses"][200]["content"]["application/json"]
    | paths["/v1/admin/partners"]["get"]["responses"][200]["content"]["application/json"]["items"][number],
): PartnerRecord {
  return {
    ...item,
    destinationIds: "destinationIds" in item ? item.destinationIds : [],
    relationshipTypes: "relationshipTypes" in item ? item.relationshipTypes : [],
  };
}

export class ApiRepository extends ApiScheduleSeriesRepository implements AdminRepository {
  async listEffectiveActionPolicies(signal?: AbortSignal) {
    const result = await apiClient.GET("/v1/admin/approval-action-policies/effective", {
      params: { header: await sessionHeaders() },
      signal: signal ?? null,
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }
  async listActionPolicies(
    page: number,
    pageSize: number,
    actionCode?: ApprovalActionCode,
    signal?: AbortSignal,
  ) {
    const result = await apiClient.GET("/v1/admin/approval-action-policies", {
      params: {
        header: await sessionHeaders(),
        query: { page, pageSize, ...(actionCode ? { actionCode } : {}) },
      },
      signal: signal ?? null,
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }
  async updateActionPolicy(
    actionCode: ApprovalActionCode,
    roleCode: ActionPolicy["roleCode"],
    input: ActionPolicyInput,
  ) {
    const result = await apiClient.PATCH(
      "/v1/admin/approval-action-policies/{actionCode}/{roleCode}",
      { body: input, params: { header: await mutationHeaders(), path: { actionCode, roleCode } } },
    );
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }
  async listExecutionControls(page: number, pageSize: number, signal?: AbortSignal) {
    const result = await apiClient.GET("/v1/admin/approval-execution-controls", {
      params: { header: await sessionHeaders(), query: { page, pageSize } },
      signal: signal ?? null,
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }
  async listSensitiveReadControls(signal?: AbortSignal) {
    const result = await apiClient.GET("/v1/admin/sensitive-projection-controls", {
      params: { header: await sessionHeaders() },
      signal: signal ?? null,
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }
  async updateSensitiveReadControl(
    projection: SensitiveProjection,
    input: SensitiveReadControlInput,
  ) {
    const result = await apiClient.PATCH("/v1/admin/sensitive-projection-controls/{projection}", {
      body: input,
      params: { header: await mutationHeaders(), path: { projection } },
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }
  async updateExecutionControl(actionCode: ApprovalActionCode, input: ExecutionControlInput) {
    const result = await apiClient.PATCH("/v1/admin/approval-execution-controls/{actionCode}", {
      body: input,
      params: { header: await mutationHeaders(), path: { actionCode } },
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }
  async withdrawApprovalRequest(requestId: string, input: ApprovalWithdrawInput) {
    const result = await apiClient.POST("/v1/admin/approval-requests/{requestId}/withdraw", {
      body: input,
      params: { header: await mutationHeaders(), path: { requestId } },
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }
  async applyApprovalRequest(requestId: string, input: ApprovalApplyInput) {
    const result = await apiClient.POST("/v1/admin/approval-requests/{requestId}/apply", {
      body: input,
      params: { header: await mutationHeaders(), path: { requestId } },
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }
  async retryApprovalRequest(requestId: string, input: ApprovalRetryInput) {
    const result = await apiClient.POST("/v1/admin/approval-requests/{requestId}/retry", {
      body: input,
      params: { header: await mutationHeaders(), path: { requestId } },
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }
  async releaseApprovalExecution(requestId: string, input: ApprovalExecutionReleaseInput) {
    const result = await apiClient.POST(
      "/v1/admin/approval-requests/{requestId}/execution-release",
      {
        body: input,
        params: { header: await mutationHeaders(), path: { requestId } },
      },
    );
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }
  async reviseApprovalRequest(requestId: string, input: ApprovalReviseInput) {
    const result = await apiClient.POST("/v1/admin/approval-requests/{requestId}/revise", {
      body: input,
      params: { header: await mutationHeaders(), path: { requestId } },
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }
  async listApprovalPolicies(signal?: AbortSignal): Promise<ApprovalPolicy[]> {
    const result = await apiClient.GET("/v1/admin/approval-policies", {
      params: { header: await sessionHeaders() },
      signal: signal ?? null,
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data.items;
  }

  async updateApprovalPolicy(
    moduleCode: ApprovalModule,
    nonAdminMode: ApprovalPolicy["nonAdminMode"],
    expectedVersion: number,
  ): Promise<ApprovalPolicy> {
    const result = await apiClient.PATCH("/v1/admin/approval-policies/{moduleCode}", {
      body: { expectedVersion, nonAdminMode },
      params: { header: await mutationHeaders(), path: { moduleCode } },
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }

  async listApprovalRequests(
    page: number,
    pageSize: number,
    filters?: ApprovalRequestFilterInput,
    signal?: AbortSignal,
  ): Promise<PageResult<ApprovalRequest>> {
    const result = await apiClient.GET("/v1/admin/approval-requests", {
      params: {
        header: await sessionHeaders(),
        query: { page, pageSize, ...(typeof filters === "string" ? { state: filters } : filters) },
      },
      signal: signal ?? null,
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }

  async getApprovalRequest(
    requestId: string,
    signal?: AbortSignal,
  ): Promise<ApprovalRequestDetail> {
    const result = await apiClient.GET("/v1/admin/approval-requests/{requestId}", {
      params: { header: await sessionHeaders(), path: { requestId } },
      signal: signal ?? null,
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }

  async createApprovalRequest(input: {
    actionCode: string;
    entityId?: string | null;
    entityType: string;
    moduleCode: ApprovalModule;
    payload: Record<string, unknown>;
    reason: string;
  }): Promise<ApprovalRequest> {
    const result = await apiClient.POST("/v1/admin/approval-requests", {
      body: input,
      params: { header: await mutationHeaders() },
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }

  async decideApprovalRequest(
    requestId: string,
    decision: "APPROVE" | "REJECT" | "REQUEST_CHANGES",
    input: { comment?: string | null; expectedDigest: string; expectedVersion: number },
  ): Promise<ApprovalRequestDetail> {
    const options = {
      body: input,
      params: { header: await mutationHeaders(), path: { requestId } },
    };
    const result =
      decision === "APPROVE"
        ? await apiClient.POST("/v1/admin/approval-requests/{requestId}/approve", options)
        : decision === "REJECT"
          ? await apiClient.POST("/v1/admin/approval-requests/{requestId}/reject", options)
          : await apiClient.POST(
              "/v1/admin/approval-requests/{requestId}/request-changes",
              options,
            );
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }

  async listNotifications(
    page: number,
    pageSize: number,
    signal?: AbortSignal,
  ): Promise<PageResult<AdminNotification> & { unread: number }> {
    const result = await apiClient.GET("/v1/admin/notifications", {
      params: { header: await sessionHeaders(), query: { page, pageSize } },
      signal: signal ?? null,
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }

  async markNotificationRead(
    notificationId: string,
    expectedVersion: number,
  ): Promise<AdminNotification> {
    const result = await apiClient.POST("/v1/admin/notifications/{notificationId}/read", {
      body: { expectedVersion },
      params: { header: await mutationHeaders(), path: { notificationId } },
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }

  async getPartnerImportDefinition(signal?: AbortSignal): Promise<PartnerImportDefinition> {
    const result = await apiClient.GET("/v1/admin/partner-imports/definition", {
      params: { header: await sessionHeaders() },
      signal: signal ?? null,
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }

  async listPartnerImportBatches(
    page: number,
    pageSize: number,
    signal?: AbortSignal,
  ): Promise<PageResult<PartnerImportBatch>> {
    const result = await apiClient.GET("/v1/admin/partner-imports/batches", {
      params: { header: await sessionHeaders(), query: { page, pageSize } },
      signal: signal ?? null,
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }

  async getPartnerImportBatch(
    batchId: string,
    signal?: AbortSignal,
    previous?: PartnerImportBatch,
  ): Promise<PartnerImportBatch> {
    const authority = captureRequestAuthority();
    const etag = importStatusValidator(previous, batchId);
    const headers = await sessionHeaders();
    signal?.throwIfAborted();
    assertRequestAuthority(authority);
    const result = await apiClient.GET("/v1/admin/partner-imports/batches/{batchId}", {
      params: { header: headers, path: { batchId } },
      ...(etag ? { headers: { "If-None-Match": etag } } : {}),
      signal: signal ?? null,
    });
    signal?.throwIfAborted();
    assertRequestAuthority(authority);
    if (result.response?.status === 304) {
      if (!previous || !etag || result.response.headers.get("etag") !== etag)
        throw new CyraError({
          code: "INVALID_RESPONSE",
          message: "Upload status could not be matched to the current view. Refresh it.",
          requestId: result.response.headers.get("x-request-id") ?? "import-status",
          status: 502,
        });
      return previous;
    }
    if (!result.data) return requestFailed(result.error, result.response);
    rememberImportStatusValidator(result.data, result.response?.headers.get("etag") ?? null);
    return result.data;
  }

  async listPartnerImportReview<K extends ImportReviewKind>(
    batchId: string,
    kind: K,
    filters: ImportFiltersByKind[K],
    investigation?: ImportInvestigation,
    signal?: AbortSignal,
  ): Promise<ImportPageByKind[K]> {
    const path = importReviewPaths[kind] as (typeof importReviewPaths)[ImportReviewKind];
    const result = await apiClient.GET(path, {
      params: {
        header: { ...(await sessionHeaders()), ...importInvestigationHeader(investigation) },
        path: { batchId },
        query: filters,
      },
      signal: signal ?? null,
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data as ImportPageByKind[K];
  }

  async getPartnerContact(contactId: string, signal?: AbortSignal): Promise<PartnerContactRecord> {
    const result = await apiClient.GET("/v1/admin/partner-contacts/{contactId}", {
      params: { header: await sessionHeaders(), path: { contactId } },
      signal: signal ?? null,
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }
  async previewPartnerContactErasure(
    contactId: string,
    signal?: AbortSignal,
  ): Promise<PartnerContactErasureInventory> {
    const result = await apiClient.GET("/v1/admin/partner-contacts/{contactId}/erasure-preview", {
      params: { header: await sessionHeaders(), path: { contactId } },
      signal: signal ?? null,
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }
  async erasePartnerContact(
    contactId: string,
    input: PartnerContactErasureInput,
  ): Promise<PartnerContactErasureStatus> {
    const result = await apiClient.POST("/v1/admin/partner-contacts/{contactId}/erase", {
      body: input,
      params: { header: await mutationHeaders(), path: { contactId } },
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }
  async getPartnerContactErasure(
    requestId: string,
    signal?: AbortSignal,
  ): Promise<PartnerContactErasureStatus> {
    const result = await apiClient.GET("/v1/admin/partner-contact-erasures/{requestId}", {
      params: { header: await sessionHeaders(), path: { requestId } },
      signal: signal ?? null,
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }
  async getPartnerContactEvidenceHold(
    contactId: string,
    signal?: AbortSignal,
  ): Promise<PartnerContactEvidenceHold> {
    const result = await apiClient.GET("/v1/admin/partner-contacts/{contactId}/evidence-hold", {
      params: { header: await sessionHeaders(), path: { contactId } },
      signal: signal ?? null,
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }
  async changePartnerContactEvidenceHold(
    contactId: string,
    input: PartnerContactEvidenceHoldInput,
  ): Promise<PartnerContactEvidenceHold> {
    const result = await apiClient.POST("/v1/admin/partner-contacts/{contactId}/evidence-hold", {
      body: input,
      params: { header: await mutationHeaders(), path: { contactId } },
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }
  async getPartnerSchedule(
    scheduleId: string,
    signal?: AbortSignal,
  ): Promise<PartnerScheduleRecord> {
    const result = await apiClient.GET("/v1/admin/partner-schedules/{scheduleId}", {
      params: { header: await sessionHeaders(), path: { scheduleId } },
      signal: signal ?? null,
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }

  async stagePartnerImport(batchId: string, input: ImportStageInput): Promise<PartnerImportBatch> {
    const result = await apiClient.POST("/v1/admin/partner-imports/batches/{batchId}/stage", {
      body: input,
      params: { header: await mutationHeaders(), path: { batchId } },
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }

  async listPartnerSetups(page: number, pageSize: number, signal?: AbortSignal) {
    const result = await apiClient.GET("/v1/admin/partner-imports/setups", {
      params: { header: await sessionHeaders(), query: { page, pageSize } },
      signal: signal ?? null,
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }
  async getPartnerSetup(
    batchId: string,
    investigation?: ImportInvestigation,
    signal?: AbortSignal,
  ) {
    const result = await apiClient.GET("/v1/admin/partner-imports/setups/{batchId}", {
      params: {
        header: { ...(await sessionHeaders()), ...importInvestigationHeader(investigation) },
        path: { batchId },
      },
      signal: signal ?? null,
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }
  async createPartnerSetup(input: import("./partner-import-models").PartnerSetupCreateInput) {
    const result = await apiClient.POST("/v1/admin/partner-imports/setups", {
      body: input,
      params: { header: await mutationHeaders() },
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }
  async savePartnerSetup(
    batchId: string,
    input: import("./partner-import-models").PartnerSetupSaveInput,
  ) {
    const result = await apiClient.POST("/v1/admin/partner-imports/setups/{batchId}/save", {
      body: input,
      params: { header: await mutationHeaders(), path: { batchId } },
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }

  async getPartnerImportSummary(batchId: string, signal?: AbortSignal) {
    const result = await apiClient.GET(
      "/v1/admin/partner-imports/batches/{batchId}/review-summary",
      { params: { header: await sessionHeaders(), path: { batchId } }, signal: signal ?? null },
    );
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }
  async locatePartnerImportRow(
    batchId: string,
    sourceRow: number,
    pageSize: number,
    signal?: AbortSignal,
  ) {
    const result = await apiClient.GET("/v1/admin/partner-imports/batches/{batchId}/locate", {
      params: { header: await sessionHeaders(), path: { batchId }, query: { sourceRow, pageSize } },
      signal: signal ?? null,
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }

  async regeneratePartnerImport(
    batchId: string,
    input: ImportRegenerateInput,
  ): Promise<PartnerImportBatch> {
    const result = await apiClient.POST("/v1/admin/partner-imports/batches/{batchId}/regenerate", {
      body: input,
      params: { header: await mutationHeaders(), path: { batchId } },
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }

  async exportPartnerImport<K extends "issues" | "results">(
    batchId: string,
    kind: K,
    filters: ImportFiltersByKind[K],
    investigation?: ImportInvestigation,
    signal?: AbortSignal,
  ): Promise<ImportCsvExport> {
    const result = await apiClient.GET(
      kind === "issues"
        ? "/v1/admin/partner-imports/batches/{batchId}/issues.csv"
        : "/v1/admin/partner-imports/batches/{batchId}/results.csv",
      {
        params: {
          header: { ...(await sessionHeaders()), ...importInvestigationHeader(investigation) },
          path: { batchId },
          query: filters,
        },
        parseAs: "text",
        signal: signal ?? null,
      },
    );
    if (result.data === undefined) return requestFailed(result.error, result.response);
    const header = (name: string) => result.response.headers.get(`x-cyra-export-${name}`);
    return {
      csv: result.data,
      page: Number(header("page") ?? filters.page ?? 1),
      pageSize: Number(header("page-size") ?? filters.pageSize ?? 20),
      total: header("total") === null ? null : Number(header("total")),
    };
  }

  async listPartnerImportChanges(
    batchId: string,
    signal?: AbortSignal,
  ): Promise<PartnerImportChange[]> {
    const result = await apiClient.GET("/v1/admin/partner-imports/batches/{batchId}/changes", {
      params: { header: await sessionHeaders(), path: { batchId } },
      signal: signal ?? null,
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data.items;
  }

  async listPartnerImportIssues(
    batchId: string,
    signal?: AbortSignal,
  ): Promise<PartnerImportIssue[]> {
    const result = await apiClient.GET("/v1/admin/partner-imports/batches/{batchId}/issues", {
      params: { header: await sessionHeaders(), path: { batchId } },
      signal: signal ?? null,
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data.items;
  }

  async createPartnerImportPreview(input: ImportPreviewInput): Promise<PartnerImportBatch> {
    const result = await apiClient.POST("/v1/admin/partner-imports/preview", {
      body: input,
      params: { header: await mutationHeaders() },
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }

  async updatePartnerImportDecision(
    changeId: string,
    decision: "APPLY" | "SKIP" | "KEEP" | "CLEAR",
    expectedBatchVersion: number,
    expectedVersion: number,
    field?: string,
  ): Promise<PartnerImportBatch> {
    const result = await apiClient.PATCH("/v1/admin/partner-imports/changes/{changeId}/decision", {
      body: {
        decision,
        expectedBatchVersion,
        expectedVersion,
        ...(field === undefined ? {} : { field }),
      },
      params: { header: await mutationHeaders(), path: { changeId } },
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }

  async applyPartnerImport(
    batchId: string,
    expectedVersion: number,
    manifestDigest: string,
    reason: string,
  ): Promise<PartnerImportBatch> {
    const result = await apiClient.POST("/v1/admin/partner-imports/batches/{batchId}/apply", {
      body: { expectedVersion, manifestDigest, reason },
      params: { header: await mutationHeaders(), path: { batchId } },
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }

  async controlPartnerImport(
    batchId: string,
    action: "cancel" | "retry",
    expectedVersion: number,
    reason: string,
  ): Promise<PartnerImportBatch> {
    const options = {
      body: { expectedVersion, reason },
      params: { header: await mutationHeaders(), path: { batchId } },
    };
    const result =
      action === "cancel"
        ? await apiClient.POST("/v1/admin/partner-imports/batches/{batchId}/cancel", options)
        : await apiClient.POST("/v1/admin/partner-imports/batches/{batchId}/retry", options);
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }

  async listDestinations(signal?: AbortSignal): Promise<DestinationRecord[]> {
    const result = await apiClient.GET("/v1/admin/destinations", {
      params: { header: await sessionHeaders() },
      signal: signal ?? null,
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data.items;
  }

  async listCanonicalCities(
    filters: ReferenceFilters,
    signal?: AbortSignal,
  ): Promise<PageResult<CanonicalCityRecord>> {
    const result = await apiClient.GET("/v1/admin/canonical-cities", {
      params: { header: await sessionHeaders(), query: filters },
      signal: signal ?? null,
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }
  async getCanonicalCity(cityId: string, signal?: AbortSignal): Promise<CanonicalCityRecord> {
    const result = await apiClient.GET("/v1/admin/canonical-cities/{cityId}", {
      params: { header: await sessionHeaders(), path: { cityId } },
      signal: signal ?? null,
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }
  async createCanonicalCity(input: CanonicalCityCreateInput): Promise<CanonicalCityRecord> {
    const result = await apiClient.POST("/v1/admin/canonical-cities", {
      params: { header: await mutationHeaders() },
      body: input,
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }
  async updateCanonicalCity(
    cityId: string,
    input: CanonicalCityUpdateInput,
  ): Promise<CanonicalCityRecord> {
    const result = await apiClient.PATCH("/v1/admin/canonical-cities/{cityId}", {
      params: { header: await mutationHeaders(), path: { cityId } },
      body: input,
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }
  async listDestinationReferences(
    filters: ReferenceFilters,
    signal?: AbortSignal,
  ): Promise<PageResult<DestinationReferenceRecord>> {
    const result = await apiClient.GET("/v1/admin/destinations", {
      params: { header: await sessionHeaders(), query: filters },
      signal: signal ?? null,
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }
  async getDestinationReference(
    destinationId: string,
    signal?: AbortSignal,
  ): Promise<DestinationReferenceRecord> {
    const result = await apiClient.GET("/v1/admin/destinations/{destinationId}", {
      params: { header: await sessionHeaders(), path: { destinationId } },
      signal: signal ?? null,
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }
  async createDestinationReference(
    input: DestinationCreateInput,
  ): Promise<DestinationReferenceRecord> {
    const result = await apiClient.POST("/v1/admin/destinations", {
      params: { header: await mutationHeaders() },
      body: input,
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }
  async updateDestinationReference(
    destinationId: string,
    input: DestinationUpdateInput,
  ): Promise<DestinationReferenceRecord> {
    const result = await apiClient.PATCH("/v1/admin/destinations/{destinationId}", {
      params: { header: await mutationHeaders(), path: { destinationId } },
      body: input,
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }
  async updateDestinationLifecycle(
    destinationId: string,
    input: DestinationLifecycleInput,
  ): Promise<DestinationReferenceRecord> {
    const result = await apiClient.POST("/v1/admin/destinations/{destinationId}/lifecycle", {
      params: { header: await mutationHeaders(), path: { destinationId } },
      body: input,
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }
  async listSystemExperiences(
    filters: ExperienceFilters,
    signal?: AbortSignal,
  ): Promise<PageResult<SystemExperienceRecord>> {
    const result = await apiClient.GET("/v1/admin/experiences", {
      params: { header: await sessionHeaders(), query: filters },
      signal: signal ?? null,
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }
  async getSystemExperience(
    experienceId: string,
    signal?: AbortSignal,
  ): Promise<SystemExperienceRecord> {
    const result = await apiClient.GET("/v1/admin/experiences/{experienceId}", {
      params: { header: await sessionHeaders(), path: { experienceId } },
      signal: signal ?? null,
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }

  async listPartners(
    filters: PartnerFilters,
    signal?: AbortSignal,
  ): Promise<PageResult<PartnerRecord>> {
    const result = await apiClient.GET("/v1/admin/partners", {
      params: {
        header: await sessionHeaders(),
        query: {
          ...(filters.tableSort
            ? { tableSort: filters.tableSort, tableDirection: filters.tableDirection ?? "asc" }
            : {}),
          ...(filters.tableFilters ? { tableFilters: filters.tableFilters } : {}),
          page: filters.page,
          pageSize: filters.pageSize,
          ...(filters.search ? { search: filters.search } : {}),
          ...(filters.lifecycleStatus === "ALL"
            ? {}
            : { lifecycleStatus: filters.lifecycleStatus }),
          ...(filters.outreachStatus === "ALL" ? {} : { outreachStatus: filters.outreachStatus }),
          ...(filters.destinationId ? { destinationId: filters.destinationId } : {}),
          ...(filters.workState ? { workState: filters.workState } : {}),
        },
      },
      signal: signal ?? null,
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return { ...result.data, items: result.data.items.map(mapPartner) };
  }

  async getPartner(partnerId: string, signal?: AbortSignal): Promise<PartnerRecord> {
    const result = await apiClient.GET("/v1/admin/partners/{partnerId}", {
      params: { header: await sessionHeaders(), path: { partnerId } },
      signal: signal ?? null,
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return mapPartner(result.data);
  }

  async createPartner(input: PartnerCreateInput): Promise<PartnerRecord> {
    const result = await apiClient.POST("/v1/admin/partners", {
      body: input,
      params: { header: await mutationHeaders() },
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return mapPartner(result.data);
  }

  async updatePartner(
    partnerId: string,
    input: PartnerUpdateInput,
    expectedVersion: number,
  ): Promise<PartnerRecord> {
    const result = await apiClient.PATCH("/v1/admin/partners/{partnerId}", {
      body: { ...input, expectedVersion },
      params: { header: await mutationHeaders(), path: { partnerId } },
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return mapPartner(result.data);
  }

  async updatePartnerLifecycle(
    partnerId: string,
    lifecycleStatus: PartnerRecord["lifecycleStatus"],
    expectedVersion: number,
    reason: string,
  ): Promise<PartnerRecord> {
    const result = await apiClient.POST("/v1/admin/partners/{partnerId}/lifecycle", {
      body: { expectedVersion, lifecycleStatus, reason },
      params: { header: await mutationHeaders(), path: { partnerId } },
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return mapPartner(result.data);
  }

  async listPartnerContacts(
    partnerId: string,
    filters: PartnerContactFilters = {},
    signal?: AbortSignal,
  ): Promise<PartnerContactPage> {
    const result = await apiClient.GET("/v1/admin/partners/{partnerId}/contacts", {
      params: { header: await sessionHeaders(), path: { partnerId }, query: filters },
      signal: signal ?? null,
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }

  async createPartnerContact(
    partnerId: string,
    input: PartnerContactInput,
  ): Promise<PartnerContactRecord> {
    const result = await apiClient.POST("/v1/admin/partners/{partnerId}/contacts", {
      body: input,
      params: { header: await mutationHeaders(), path: { partnerId } },
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }

  async updatePartnerContact(
    contactId: string,
    input: PartnerContactUpdateInput,
    expectedVersion: number,
  ): Promise<PartnerContactRecord> {
    const result = await apiClient.PATCH("/v1/admin/partner-contacts/{contactId}", {
      body: { ...input, expectedVersion },
      params: { header: await mutationHeaders(), path: { contactId } },
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }

  async updatePartnerContactLifecycle(
    contactId: string,
    lifecycleStatus: PartnerContactLifecycleTarget,
    expectedVersion: number,
    reason: string,
    expectedPartnerVersion: number,
  ): Promise<PartnerContactRecord> {
    assertContactLifecycleTarget(lifecycleStatus);
    const result = await apiClient.POST("/v1/admin/partner-contacts/{contactId}/lifecycle", {
      body: { expectedPartnerVersion, expectedVersion, lifecycleStatus, reason },
      params: { header: await mutationHeaders(), path: { contactId } },
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }

  async listExperiences(signal?: AbortSignal): Promise<ExperienceRecord[]> {
    const result = await apiClient.GET("/v1/admin/experiences", {
      params: { header: await sessionHeaders(), query: { page: 1, pageSize: 50 } },
      signal: signal ?? null,
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data.items;
  }

  async createExperience(input: ExperienceCreateInput): Promise<ExperienceRecord> {
    const result = await apiClient.POST("/v1/admin/experiences", {
      body: input,
      params: { header: await mutationHeaders() },
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }

  async updateExperience(
    experienceId: string,
    input: ExperienceInput,
    expectedVersion: number,
  ): Promise<ExperienceRecord> {
    const result = await apiClient.PATCH("/v1/admin/experiences/{experienceId}", {
      body: { ...input, expectedVersion },
      params: { header: await mutationHeaders(), path: { experienceId } },
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }

  async updateExperienceLifecycle(
    experienceId: string,
    lifecycleStatus: ExperienceRecord["lifecycleStatus"],
    expectedVersion: number,
    reason: string,
  ): Promise<ExperienceRecord> {
    const result = await apiClient.POST("/v1/admin/experiences/{experienceId}/lifecycle", {
      body: { expectedVersion, lifecycleStatus, reason },
      params: { header: await mutationHeaders(), path: { experienceId } },
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }

  async listPartnerOfferings(
    partnerId: string,
    filters: PartnerOfferingFilters = {},
    signal?: AbortSignal,
  ): Promise<PartnerOfferingPage> {
    const result = await apiClient.GET("/v1/admin/partners/{partnerId}/offerings", {
      params: { header: await sessionHeaders(), path: { partnerId }, query: filters },
      signal: signal ?? null,
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }

  async getPartnerOffering(
    offeringId: string,
    signal?: AbortSignal,
  ): Promise<PartnerOfferingRecord> {
    const result = await apiClient.GET("/v1/admin/partner-offerings/{offeringId}", {
      params: { header: await sessionHeaders(), path: { offeringId } },
      signal: signal ?? null,
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }

  async createPartnerOffering(
    partnerId: string,
    input: PartnerOfferingCreateInput,
  ): Promise<PartnerOfferingRecord> {
    const result = await apiClient.POST("/v1/admin/partners/{partnerId}/offerings", {
      body: input,
      params: { header: await mutationHeaders(), path: { partnerId } },
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }

  async updatePartnerOffering(
    offeringId: string,
    input: PartnerOfferingInput,
    expectedVersion: number,
  ): Promise<PartnerOfferingRecord> {
    const result = await apiClient.PATCH("/v1/admin/partner-offerings/{offeringId}", {
      body: { ...input, expectedVersion },
      params: { header: await mutationHeaders(), path: { offeringId } },
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }

  async updatePartnerOfferingLifecycle(
    offeringId: string,
    lifecycleStatus: PartnerOfferingRecord["lifecycleStatus"],
    expectedVersion: number,
    reason: string,
  ): Promise<PartnerOfferingRecord> {
    const result = await apiClient.POST("/v1/admin/partner-offerings/{offeringId}/lifecycle", {
      body: { expectedVersion, lifecycleStatus, reason },
      params: { header: await mutationHeaders(), path: { offeringId } },
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }

  async listPartnerSchedules(
    offeringId: string,
    filters: PartnerScheduleFilters = {},
    signal?: AbortSignal,
  ): Promise<PartnerSchedulePage> {
    const result = await apiClient.GET("/v1/admin/partner-offerings/{offeringId}/schedules", {
      params: { header: await sessionHeaders(), path: { offeringId }, query: filters },
      signal: signal ?? null,
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }

  async listScheduleOverview(
    filters: ScheduleOverviewFilters,
    signal?: AbortSignal,
  ): Promise<ScheduleOverviewPage> {
    const result = await apiClient.GET("/v1/admin/partner-schedules", {
      params: { header: await sessionHeaders(), query: filters },
      signal: signal ?? null,
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }

  async listPartnerHistory(
    partnerId: string,
    filters: PartnerHistoryFilters = {},
    signal?: AbortSignal,
  ): Promise<PartnerHistoryPage> {
    const result = await apiClient.GET("/v1/admin/partners/{partnerId}/history", {
      params: { header: await sessionHeaders(), path: { partnerId }, query: filters },
      signal: signal ?? null,
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }

  async listExperienceOfferings(
    experienceId: string,
    filters: ExperienceOfferingFilters = {},
    signal?: AbortSignal,
  ): Promise<ExperienceOfferingPage> {
    const result = await apiClient.GET("/v1/admin/experiences/{experienceId}/offerings", {
      params: { header: await sessionHeaders(), path: { experienceId }, query: filters },
      signal: signal ?? null,
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }

  async createPartnerSchedule(
    offeringId: string,
    input: PartnerScheduleCreateInput,
  ): Promise<PartnerScheduleRecord> {
    const result = await apiClient.POST("/v1/admin/partner-offerings/{offeringId}/schedules", {
      body: input,
      params: { header: await mutationHeaders(), path: { offeringId } },
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }

  async previewPartnerSchedule(
    offeringId: string,
    input: PartnerSchedulePreviewInput,
    signal?: AbortSignal,
  ): Promise<PartnerSchedulePreview> {
    const result = await apiClient.POST(
      "/v1/admin/partner-offerings/{offeringId}/schedule-preview",
      {
        body: input,
        params: { header: await sessionHeaders(), path: { offeringId } },
        signal: signal ?? null,
      },
    );
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }

  async updatePartnerSchedule(
    scheduleId: string,
    input: PartnerScheduleInput,
    expectedOfferingVersion: number,
    expectedVersion: number,
  ): Promise<PartnerScheduleRecord> {
    const result = await apiClient.PATCH("/v1/admin/partner-schedules/{scheduleId}", {
      body: { ...input, expectedOfferingVersion, expectedVersion },
      params: { header: await mutationHeaders(), path: { scheduleId } },
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }

  async updatePartnerScheduleStatus(
    scheduleId: string,
    action: "archive" | "cancel",
    expectedOfferingVersion: number,
    expectedVersion: number,
    reason: string,
  ): Promise<PartnerScheduleRecord> {
    const options = {
      body: { expectedOfferingVersion, expectedVersion, reason },
      params: { header: await mutationHeaders(), path: { scheduleId } },
    };
    const result =
      action === "cancel"
        ? await apiClient.POST("/v1/admin/partner-schedules/{scheduleId}/cancel", options)
        : await apiClient.POST("/v1/admin/partner-schedules/{scheduleId}/archive", options);
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }
  async getDashboardSummary(
    filters: DashboardFilters,
    signal?: AbortSignal,
  ): Promise<DashboardSummary> {
    const result = await apiClient.GET("/v1/admin/dashboard", {
      params: { header: await sessionHeaders(), query: filters },
      signal: signal ?? null,
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }

  async listUsers(filters: UserFilters, signal?: AbortSignal): Promise<PageResult<UserRecord>> {
    const result = await apiClient.GET("/v1/admin/users", {
      params: {
        header: await sessionHeaders(),
        query: {
          ...(filters.tableSort
            ? { tableSort: filters.tableSort, tableDirection: filters.tableDirection ?? "asc" }
            : {}),
          ...(filters.tableFilters ? { tableFilters: filters.tableFilters } : {}),
          page: filters.page,
          pageSize: filters.pageSize,
          direction: filters.sortDirection,
          ...(filters.audience ? { audience: filters.audience } : {}),
          sort: filters.sort,
          ...(filters.search ? { search: filters.search } : {}),
          ...(filters.state === "ALL" ? {} : { state: filters.state }),
        },
      },
      signal: signal ?? null,
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return {
      items: result.data.items.map(baseUser),
      ...(result.data.filterOptions ? { filterOptions: result.data.filterOptions } : {}),
      page: result.data.page,
      pageSize: result.data.pageSize,
      total: result.data.total,
    };
  }

  async getUser(userId: string, signal?: AbortSignal): Promise<UserRecord> {
    const detailResult = await apiClient.GET("/v1/admin/users/{userId}", {
      params: { header: await sessionHeaders(), path: { userId } },
      signal: signal ?? null,
    });
    if (!detailResult.data) return requestFailed(detailResult.error, detailResult.response);
    const user = baseUser(detailResult.data);
    user.activeSessionCount = detailResult.data.activeSessions;
    user.profileImageAssetId = detailResult.data.profileImageAssetId;
    return user;
  }

  async listUserSessions(userId: string, signal?: AbortSignal): Promise<SessionRecord[]> {
    const result = await apiClient.GET("/v1/admin/users/{userId}/sessions", {
      params: { header: await sessionHeaders(), path: { userId } },
      signal: signal ?? null,
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data.items.map(
      (session): SessionRecord => ({
        device: "Device details redacted",
        id: session.id,
        lastActivityAt: session.lastActivityAt,
        state: session.state,
      }),
    );
  }

  async listUserLegalAcceptances(userId: string, signal?: AbortSignal): Promise<LegalAcceptance[]> {
    const result = await apiClient.GET("/v1/admin/users/{userId}/legal-acceptances", {
      params: { header: await sessionHeaders(), path: { userId } },
      signal: signal ?? null,
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data.items;
  }

  async listAdministrators(signal?: AbortSignal): Promise<UserRecord[]> {
    const result = await apiClient.GET("/v1/admin/administrators", {
      params: { header: await sessionHeaders() },
      signal: signal ?? null,
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data.items.map(mapAdministrator);
  }

  async getInvitation(invitationId: string, signal?: AbortSignal): Promise<InvitationRecord> {
    const result = await apiClient.GET("/v1/admin/user-invitations/{invitationId}", {
      params: { header: await sessionHeaders(), path: { invitationId } },
      signal: signal ?? null,
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return mapInvitation(result.data);
  }

  async listInvitations(
    filters: InvitationFilters = defaultInvitationFilters,
    signal?: AbortSignal,
  ): Promise<InvitationPage> {
    const result = await apiClient.GET("/v1/admin/user-invitations", {
      params: { header: await sessionHeaders(), query: filters },
      signal: signal ?? null,
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return { ...result.data, items: result.data.items.map(mapInvitation) };
  }

  async listRoles(signal?: AbortSignal): Promise<RoleDefinition[]> {
    const result = await apiClient.GET("/v1/admin/roles", {
      params: { header: await sessionHeaders() },
      signal: signal ?? null,
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data.items
      .filter((item) => roleCodes.has(item.code as RoleCode))
      .map((item) => ({
        code: item.code as RoleCode,
        description: item.description,
        label: item.code.replaceAll("_", " "),
        permissions: item.permissions,
        requiresMfa: item.requiresMfa,
        runtimeEnabled: item.runtimeEnabled,
      }));
  }

  async listLegalDocuments(signal?: AbortSignal): Promise<LegalDocument[]> {
    const result = await apiClient.GET("/v1/admin/legal-document-versions", {
      params: { header: await sessionHeaders() },
      signal: signal ?? null,
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data.items.map(mapLegal);
  }

  async getLegalDocument(documentId: string, signal?: AbortSignal): Promise<LegalDocument | null> {
    const result = await apiClient.GET("/v1/admin/legal-document-versions/{documentId}", {
      params: { header: await sessionHeaders(), path: { documentId } },
      signal: signal ?? null,
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return mapLegal(result.data);
  }

  async listAuditTrail(
    filters: AuditTrailFilters,
    signal?: AbortSignal,
  ): Promise<PageResult<AuditTrailItem>> {
    const result = await apiClient.GET("/v1/admin/audit-trail", {
      params: {
        header: await sessionHeaders(),
        query: {
          page: filters.page,
          pageSize: filters.pageSize,
          includeSystem: filters.includeSystem ? "true" : "false",
          ...(filters.snapshot ? { snapshot: filters.snapshot } : {}),
          ...(filters.tableSort
            ? { tableSort: filters.tableSort, tableDirection: filters.tableDirection }
            : {}),
          ...(filters.tableFilters ? { tableFilters: filters.tableFilters } : {}),
          ...(filters.actorType === "ALL" ? {} : { actorType: filters.actorType }),
          ...(filters.category === "ALL" ? {} : { category: filters.category }),
          ...(filters.outcome === "ALL" ? {} : { outcome: filters.outcome }),
          ...(filters.search.trim() ? { search: filters.search.trim() } : {}),
          ...(filters.from === undefined ? {} : { from: filters.from }),
          ...(filters.to === undefined ? {} : { to: filters.to }),
        },
      },
      signal: signal ?? null,
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return {
      items: result.data.items.map(mapAuditTrail),
      ...(result.data.snapshot ? { snapshot: result.data.snapshot } : {}),
      ...(result.data.filterOptions ? { filterOptions: result.data.filterOptions } : {}),
      page: result.data.page,
      pageSize: result.data.pageSize,
      total: result.data.total,
    };
  }

  async getAuditTrailDetail(
    interactionId: string,
    signal?: AbortSignal,
  ): Promise<AuditTrailDetail> {
    const result = await apiClient.GET("/v1/admin/audit-trail/{interactionId}", {
      params: { header: await sessionHeaders(), path: { interactionId } },
      signal: signal ?? null,
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }

  async mutateAccess(
    userId: string,
    action: "suspend" | "restore" | "ban",
    expectedVersion: number,
    reason: string,
  ): Promise<UserMutationReceipt> {
    const options = {
      body: { expectedVersion, reason },
      params: { header: await mutationHeaders(), path: { userId } },
    };
    const result =
      action === "suspend"
        ? await apiClient.POST("/v1/admin/users/{userId}/suspend", options)
        : action === "restore"
          ? await apiClient.POST("/v1/admin/users/{userId}/restore", options)
          : await apiClient.POST("/v1/admin/users/{userId}/ban", options);
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }

  async revokeSessions(
    userId: string,
    reason: string,
    expectedVersion: number,
  ): Promise<UserMutationReceipt> {
    const result = await apiClient.POST("/v1/admin/users/{userId}/sessions/revoke", {
      body: { reason, expectedVersion },
      params: { header: await mutationHeaders(), path: { userId } },
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }

  async inviteUser(input: InviteInput): Promise<InvitationRecord> {
    const result = await apiClient.POST("/v1/admin/user-invitations", {
      body: {
        email: input.email,
        firstName: input.firstName,
        lastName: input.lastName,
        optionalRoles: input.roles.filter((role) => role !== "USER"),
        reason: input.reason,
      },
      params: { header: await mutationHeaders() },
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return mapInvitation(result.data);
  }

  async listPartnerTeam(partnerId: string, signal?: AbortSignal): Promise<PartnerTeamMember[]> {
    const result = await apiClient.GET("/v1/admin/partners/{partnerId}/team", {
      params: { header: await sessionHeaders(), path: { partnerId } },
      signal: signal ?? null,
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data.items.map(mapPartnerTeamMember);
  }

  async invitePartnerContentManager(
    partnerId: string,
    input: PartnerTeamInviteInput,
  ): Promise<ApprovalRequest> {
    const result = await apiClient.POST("/v1/admin/partners/{partnerId}/team-invitations", {
      body: input,
      params: { header: await mutationHeaders(), path: { partnerId } },
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }

  async changePartnerTeamMembership(
    partnerId: string,
    membershipId: string,
    action: "suspend" | "remove",
    input: PartnerTeamMembershipInput,
  ): Promise<PartnerTeamMember> {
    const params = {
      header: await mutationHeaders(),
      path: { membershipId, partnerId },
    };
    const result =
      action === "suspend"
        ? await apiClient.POST("/v1/admin/partners/{partnerId}/team/{membershipId}/suspend", {
            body: input,
            params,
          })
        : await apiClient.POST("/v1/admin/partners/{partnerId}/team/{membershipId}/remove", {
            body: input,
            params,
          });
    if (!result.data) return requestFailed(result.error, result.response);
    return mapPartnerTeamMember(result.data);
  }

  async resendInvitation(
    invitationId: string,
    expectedVersion: number,
    reason: string,
  ): Promise<InvitationRecord> {
    const result = await apiClient.POST("/v1/admin/user-invitations/{invitationId}/resend", {
      body: { expectedVersion, reason },
      params: { header: await mutationHeaders(), path: { invitationId } },
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return mapInvitation(result.data);
  }

  async cancelInvitation(
    invitationId: string,
    expectedVersion: number,
    reason: string,
  ): Promise<InvitationRecord> {
    const result = await apiClient.POST("/v1/admin/user-invitations/{invitationId}/cancel", {
      body: { expectedVersion, reason },
      params: { header: await mutationHeaders(), path: { invitationId } },
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return mapInvitation(result.data);
  }

  async updateRoles(
    userId: string,
    nextRoles: RoleCode[],
    reason: string,
    expectedVersion: number,
  ): Promise<UserMutationReceipt> {
    const result = await apiClient.PUT("/v1/admin/users/{userId}/roles", {
      body: {
        expectedVersion,
        reason,
        roles: nextRoles.filter(
          (role): role is "ADMIN" | "MODERATOR" | "CONTENT_EDITOR" => role !== "USER",
        ),
      },
      params: { header: await mutationHeaders(), path: { userId } },
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }

  async mutateAdministrator(
    userId: string,
    action: "suspend" | "restore",
    expectedVersion: number,
    reason: string,
  ): Promise<UserMutationReceipt> {
    const options = {
      body: { expectedVersion, reason },
      params: { header: await mutationHeaders(), path: { userId } },
    };
    const result =
      action === "suspend"
        ? await apiClient.POST("/v1/admin/administrators/{userId}/suspend", options)
        : await apiClient.POST("/v1/admin/administrators/{userId}/restore", options);
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }

  async resetAdministratorMfa(
    userId: string,
    reason: string,
    expectedVersion: number,
  ): Promise<UserMutationReceipt> {
    const result = await apiClient.POST("/v1/admin/administrators/{userId}/reset-mfa", {
      body: { confirmation: "RESET_MFA", expectedVersion, reason },
      params: { header: await mutationHeaders(), path: { userId } },
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return result.data;
  }

  async saveLegalDraft(
    input: LegalDraftInput,
    documentId?: string,
    expectedVersion?: number,
  ): Promise<LegalDocument> {
    const body = {
      body: input.bodyHtml,
      effectiveAt: input.effectiveAt,
      kind: input.kind === "terms" ? ("TERMS" as const) : ("PRIVACY" as const),
      semanticVersion: input.version,
      title: input.title,
    };
    if (documentId) {
      if (!expectedVersion) throw new Error("The draft revision is required.");
      const result = await apiClient.PATCH("/v1/admin/legal-document-drafts/{documentId}", {
        body: { ...body, expectedVersion },
        params: { header: await mutationHeaders(), path: { documentId } },
      });
      if (!result.data) return requestFailed(result.error, result.response);
      return mapLegal(result.data);
    }
    const result = await apiClient.POST("/v1/admin/legal-document-drafts", {
      body,
      params: { header: await mutationHeaders() },
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return mapLegal(result.data);
  }

  async createLegalRevision(documentId: string, expectedVersion: number): Promise<LegalDocument> {
    const result = await apiClient.POST(
      "/v1/admin/legal-document-versions/{documentId}/draft-revision",
      {
        body: { expectedVersion },
        params: { header: await mutationHeaders(), path: { documentId } },
      },
    );
    if (!result.data) return requestFailed(result.error, result.response);
    return mapLegal(result.data);
  }

  async publishLegal(
    documentId: string,
    expectedVersion: number,
    reason: string,
  ): Promise<LegalDocument> {
    const result = await apiClient.POST("/v1/admin/legal-document-drafts/{documentId}/publish", {
      body: {
        confirmation: "PUBLISH",
        expectedVersion,
        reason,
      },
      params: { header: await mutationHeaders(), path: { documentId } },
    });
    if (!result.data) return requestFailed(result.error, result.response);
    return mapLegal(result.data);
  }
}

export const apiRepository = new ApiRepository();

import type { ImportPreviewInput } from "./partner-import-models";
